/**
 * Centralized PDF Header Generator
 * 
 * This is the single source of truth for generating PDF headers.
 * All export functions (exam, research, etc.) should import and use this.
 */

import { HeaderSettings } from './types';

/**
 * Generates complete HTML for the PDF header based on saved settings.
 * Returns empty string if header is disabled.
 */
export const generatePdfHeader = (settings?: HeaderSettings): string => {
    if (!settings || !settings.isEnabled) {
        return '';
    }

    const { logo, schoolName, motto, address, separator, layout, spacing, scale } = settings;
    const activeScale = scale || 1;

    // Determine text alignment based on layout
    const textAlign = layout === 'centered' ? 'center' : 'left';

    // Container Style
    // We use a relative container to support absolute positioning of elements (like custom logo)
    // and scaling transforms.
    let html = `
        <div style="
            width: 100%;
            margin-top: ${spacing?.marginTop ?? 20}px;
            margin-bottom: ${spacing?.marginBottom ?? 0}px;
            position: relative;
            transform: scale(${activeScale});
            transform-origin: top center;
            border-bottom: none;
            page-break-inside: avoid;
            font-family: 'Inter', sans-serif; 
        ">
    `;

    // --- LOGO RENDERING ---
    // If custom positioning is enabled, render logo absolutely.
    // Otherwise, it will be rendered inside the content structure below.
    const renderLogoAbsolute = () => {
        if (!logo.url || !logo.customPositioning) return '';
        return `
            <div style="
                position: absolute;
                left: ${logo.x}px;
                top: ${logo.y}px;
                width: ${logo.width}px;
                z-index: 10;
            ">
                 <img src="${logo.url}" style="width: 100%; height: auto; display: block;" alt="Logo" />
            </div>
        `;
    };

    // Helper for Font Family
    const getFontStack = (family: string) => {
        if (family === 'serif') return 'Georgia, Times, serif';
        if (family === 'sans') return 'Arial, Helvetica, sans-serif';
        if (family === 'mono') return 'Courier New, monospace';
        return `'${family}', sans-serif`;
    };

    const textFontStack = getFontStack(schoolName.fontFamily);

    // --- TEXT BLOCKS ---
    const renderSchoolName = () => `
        <h1 style="
            margin: 0;
            font-size: ${schoolName.fontSize}px;
            font-weight: ${schoolName.bold ? 'bold' : 'normal'};
            color: ${schoolName.color};
            font-family: ${textFontStack};
            line-height: 1.2;
            text-align: ${schoolName.alignment || textAlign};  /* Honor specific alignment if set, else layout default */
        ">${schoolName.text}</h1>
    `;

    const renderMotto = () => {
        if (!motto.text) return '';
        return `
        <div style="
            margin: 5px 0;
            font-style: ${motto.italic ? 'italic' : 'normal'};
            font-weight: ${motto.bold ? 'bold' : 'normal'};
            color: ${motto.color};
            text-align: ${motto.alignment || textAlign};
            font-size: 14px;
        ">${motto.text}</div>`;
    };

    const renderAddress = () => `
        <div style="
            margin: 5px 0;
            font-size: ${address.fontSize}px;
            color: ${address.color};
            text-align: ${address.alignment || textAlign};
            white-space: pre-line;
            line-height: ${address.lineHeight};
        ">${address.text}</div>
    `;

    // Add Absolute Logo
    html += renderLogoAbsolute();

    // --- SEPARATOR SECTION ---
    const renderSeparator = () => {
        if (!separator?.enabled) return '';

        // Defaults matching CustomHeader.tsx
        const marginTop = separator.marginTop ?? 10;
        const marginBottom = separator.marginBottom ?? 10;
        const thickness = separator.thickness ?? 1;
        const color = separator.color ?? '#e2e8f0';

        return `
            <div style="
                margin-top: ${marginTop}px;
                margin-bottom: ${marginBottom}px;
                border-bottom: ${thickness}px solid ${color};
                width: 100%;
                font-size: 0;
                line-height: 0;
            "></div>
        `;
    };

    // --- MAIN LAYOUT STRUCTURE ---
    if (layout === 'centered') {
        html += `
            <div style="display: flex; flex-direction: column; align-items: center; text-align: center; width: 100%;">
                 ${!logo.customPositioning && logo.url ? `
                    <div style="margin-bottom: 16px;">
                        <img src="${logo.url}" style="width: ${logo.width}px; height: auto;" alt="Logo"/>
                    </div>
                 ` : ''}
                 <div style="width: 100%; max-width: 800px;">
                    ${renderSchoolName()}
                    ${renderMotto()}
                    ${renderAddress()}
                 </div>
            </div>
            ${renderSeparator()}
        `;
    }
    else if (layout === 'two-column') {
        html += `
            <div style="display: flex; flex-direction: column; width: 100%;">
                <div style="display: flex; justify-content: space-between; align-items: flex-start; width: 100%;">
                    <div style="display: flex; align-items: flex-start;">
                        ${!logo.customPositioning && logo.url ? `
                            <div style="margin-right: 16px;">
                                <img src="${logo.url}" style="width: ${logo.width}px; height: auto;" alt="Logo"/>
                            </div>
                        ` : ''}
                        <div style="text-align: left;">
                            ${renderSchoolName()}
                            ${renderMotto()}
                        </div>
                    </div>
                    <div style="text-align: right; flex: 1;">
                        ${renderAddress()}
                    </div>
                </div>
                ${renderSeparator()}
            </div>
        `;
    }
    else {
        // Classic Layout
        html += `
            <div style="display: flex; flex-direction: column; width: 100%;">
                <div style="display: flex; align-items: center; width: 100%;">
                    ${!logo.customPositioning && logo.url ? `
                        <div style="margin-right: 24px; flex-shrink: 0;">
                            <img src="${logo.url}" style="width: ${logo.width}px; height: auto;" alt="Logo"/>
                        </div>
                    ` : ''}
                    <div style="flex: 1;">
                        ${renderSchoolName()}
                        ${renderMotto()}
                        ${renderAddress()}
                    </div>
                </div>
                ${renderSeparator()}
            </div>
        `;
    }

    html += `</div>`;
    return html;
};

/**
 * Gets Google Fonts import link if custom fonts are used.
 */
export const getFontImports = (settings?: HeaderSettings): string => {
    if (!settings?.isEnabled) return '';

    const fontFamily = settings.schoolName.fontFamily;
    if (fontFamily && fontFamily !== 'serif' && fontFamily !== 'sans' && fontFamily !== 'mono') {
        return `<link href="https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@400;700&display=swap" rel="stylesheet">`;
    }
    return '';
};
